import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
	alpha,
	Autocomplete,
	Chip,
	CircularProgress,
	Divider,
	Grid,
	InputAdornment,
	ListItem,
	ListItemButton,
	ListItemText,
	Paper,
	styled,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';

import { useTranslation } from 'react-i18next';

import { IconComponent } from '@zippeditoolsjs/zippedi-icons';

const StyledTextField = styled(
	props => {
		return (<TextField {...props} />)
	}, { shouldForwardProp: props => props !== 'color' }
)(({ theme, searchbartheme }) => {
	const currentTheme = searchbartheme === 'default' ? theme.palette.type : searchbartheme;
	return ({
		// Textfield's border
		'& .MuiOutlinedInput-root': {
			background: currentTheme === 'light' ? theme.palette.common.white : '',
			color: currentTheme === 'light' ? theme.palette.text.primary : theme.palette.common.white,
			height: 'auto',
			'&:hover fieldset': {
				borderColor: theme.palette.grey[300],
			},
			'&.Mui-focused fieldset': {
				borderColor: theme.palette.grey[300],
			},
		},
		'& .MuiOutlinedInput-notchedOutline': {
			borderColor: currentTheme === 'light' ? '' : theme.palette.common.white,
		},
		// 'X' adornment for clearing the input
		'& .MuiAutocomplete-clearIndicator': {
			color: currentTheme === 'light' ? theme.palette.text.primary : theme.palette.common.white,
			'&:hover': {
				backgroundColor: currentTheme === 'light' ? theme.palette.action.hover : theme.palette.grey[800],
			},
		},
		// Search icon
		'& .MuiInputAdornment-root': {
			color: theme.palette.grey[500],
		},
	})
});


const ISO_DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;

// Read as a local day: "2026-09-15" would otherwise be parsed as UTC and shift a day.
const toLocalDay = (value) => {
	if (!value) return null;
	if (value instanceof Date) {
		return isNaN(value.getTime()) ? null : new Date(value.getFullYear(), value.getMonth(), value.getDate());
	}
	const isoDay = String(value).match(ISO_DAY_PATTERN);
	if (isoDay) {
		const [, year, month, day] = isoDay;
		return new Date(Number(year), Number(month) - 1, Number(day));
	}
	const parsed = new Date(value);
	return isNaN(parsed.getTime()) ? null : new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const LastTimeSeen = ({ lastTimeSeen, sessionDate }) => {
	const { t } = useTranslation();
	const seenDay = toLocalDay(lastTimeSeen);
	const sessionDay = toLocalDay(sessionDate);

	if (!seenDay) return null;

	const seenDate = seenDay.toLocaleDateString();
	const isOutdated = Boolean(sessionDay) && seenDay.getTime() !== sessionDay.getTime();

	if (!isOutdated) {
		return (
			<>
				<Typography variant="caption" color="blue">{"Last time seen: "}</Typography>
				<Typography variant="caption" color="text.primary">{seenDate}</Typography>
			</>
		);
	}

	return (
		<Tooltip
			title={t(
				'cws_app.Walls.not_seen_on_session',
				'This product was not scanned on {{sessionDate}}. Last time seen: {{seenDate}}.',
				{
					sessionDate: sessionDay.toLocaleDateString(),
					seenDate,
					// i18next escapes the date separators unless interpolation is opted out.
					interpolation: { escapeValue: false },
				},
			)}
		>
			<Chip
				size="small"
				variant="outlined"
				icon={<IconComponent iconName={'warning-outline'} style={{ fontSize: '14px' }} />}
				label={t('cws_app.Walls.seen_on_date', 'Seen {{date}}', {
					date: seenDate,
					interpolation: { escapeValue: false },
				})}
				sx={theme => ({
					height: 'auto',
					py: 0.25,
					color: theme.palette.warning.dark,
					borderColor: theme.palette.warning.main,
					backgroundColor: alpha(theme.palette.warning.main, 0.12),
					'& .MuiChip-icon': { color: 'inherit', ml: 0.75, mr: -0.25 },
					'& .MuiChip-label': { px: 0.75, ...theme.typography.caption },
				})}
			/>
		</Tooltip>
	);
};

const NoSearchResults = () => {
	const { t } = useTranslation();
	return (
		<Grid container direction="row" flexWrap="nowrap" alignItems="flex-start" gap={1} p={1.5}>
			<Grid item display="flex" color="text.secondary" pt={0.25}>
				<IconComponent iconName={'information-circle-outline'} style={{ fontSize: '18px' }} />
			</Grid>
			<Grid item>
				<Typography variant="body2" color="text.secondary">
					{t('cws_app.Walls.no_search_results',
						'No results were found for this product in the selected session. It may not exist yet, the aisle where it was last seen may not have been scanned in this session, or it may have last been seen more than two weeks ago.')}
				</Typography>
			</Grid>
		</Grid>
	);
};

const ResultsDisplay = ({ option, setProduct, sessionDate }) => {
	const { description, ean, item, aisle, last_time_seen } = option;
	return (
		<>
				<ListItem alignItems="flex-start" disablePadding clickeable onClick={() => { setProduct(option) }} >
					<ListItemButton>
						<ListItemText
							disableTypography
							primary={
								<Typography color="text.primary">{description}</Typography>
							}
							secondary={
								<Grid container direction="row" justifyContent="space-between" alignItems="center">
									<Grid item>
										<Typography variant="caption" color="text.primary">{"SAP: "}</Typography>
										<Typography variant="caption" color="text.primary">{item}</Typography>
										&nbsp;&nbsp;&nbsp;&nbsp;
										{ean && ean.length > 1 ?
											<>
												<Typography variant="caption" color="text.primary">{"Barcode: "}</Typography>
												<Typography variant="caption" color="text.primary">{ean}</Typography>
											</>
											: null
										}
									</Grid>
									<Grid item>
										<Typography variant="caption" color="blue">{"Aisle: "}</Typography>
										<Typography variant="caption" color="text.primary">{aisle}</Typography>
										&nbsp;&nbsp;&nbsp;&nbsp;
										<LastTimeSeen lastTimeSeen={last_time_seen} sessionDate={sessionDate} />
									</Grid>
								</Grid>
							}
						/>
					</ListItemButton>
				</ListItem>
				<Divider />
			</>
		);
	}


export default function ShelfSearch(props) {
	const {
		aislesList,
		searchResults,
		isLoadingSearchResults,
		getSearchResults,
		selectedStore,
		supplier,
		sessionDate,
		product,
		setProduct,
		disabled,
		color = 'secondary',
		searchbartheme = 'default',
	} = props;

	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [inputValue, setInputValue] = useState('');
	const [controlledOptions, setControlledOptions] = useState([]);
	const [hasSearched, setHasSearched] = useState(false);
	const loading = open && isLoadingSearchResults;
	const showNoResults = hasSearched && !isLoadingSearchResults && controlledOptions.length === 0;

	useEffect(() => {
		if (searchResults?.length) {
			setControlledOptions(searchResults);
		} else {
			setControlledOptions([]);
		}
	}, [searchResults]);

	useEffect(() => {
		if (selectedStore && Object.keys(selectedStore).length > 0) {
			setControlledOptions([]);
			setHasSearched(false);
		}
	}, [selectedStore]);

	const SearchResultsPaper = useMemo(() => (
		({ children, ...paperProps }) => (
			<Paper {...paperProps}>
				{children}
				{showNoResults ? <NoSearchResults /> : null}
			</Paper>
		)
	), [showNoResults]);

	const handleSelectProduct = (newValue) => {
		setProduct(newValue);
		setOpen(!open);
	}

	return (
		<Autocomplete
			disabled={disabled}

			fullWidth
			freeSolo

			value={product}
			onChange={(event, newValue) => {
				setProduct(newValue);
			}}
			inputValue={inputValue}
			onInputChange={(event, newInputValue) => {
				setInputValue(newInputValue);
				setHasSearched(false);
			}}
			filterOptions={(x) => x}
			PaperComponent={SearchResultsPaper}
			sx={{ minWidth: 300 }}

			open={open}
			onOpen={() => { setOpen(true) }}
			onClose={() => { setOpen(false) }}

			isOptionEqualToValue={(option, value) => (option?.ean === value?.ean) || (option?.item === value?.item) || (option?.texts === value?.description)}
			getOptionLabel={(option) => option?.description}

			options={controlledOptions}
			loading={loading}

			renderInput={(params) =>
				<StyledTextField {...params}
					variant="outlined"
					color={color}
					searchbartheme={searchbartheme}
					placeholder={disabled ? t('cws_app.general.sel_one_store') : t('cws_app.general.search')}
					InputProps={{
						...params.InputProps,
						startAdornment: (
							<>
								{loading ? <CircularProgress color="inherit" size={20} /> : null}
								{params.InputProps.endAdornment}
								<InputAdornment position="start">
									<IconComponent
										iconName={'search-outline'}
										style={{ fontSize: '20px' }}
									/>
								</InputAdornment>
							</>
						)
					}}
				/>
			}
			renderOption={(props, option) => (
				<React.Fragment>
					<ResultsDisplay option={option} setProduct={handleSelectProduct} sessionDate={sessionDate} />
				</React.Fragment>
			)}
			onKeyDown={(event) => {
				if (event.key === 'Enter') {
					// Prevent's default 'Enter' behavior.
					event.defaultMuiPrevented = true;
					if (event.target.value.length > 2 && selectedStore && Object.keys(selectedStore).length > 0) {
						setHasSearched(true);
						getSearchResults(
							selectedStore?.store_code,
							supplier ? supplier : null,
							aislesList ? aislesList.map(aisleRow => aisleRow?.code).filter(aisleCode => aisleCode) : [],
							event.target.value,
							null,
							null
						);
					}
				}
			}}

		/>
	);
}
